import { z } from 'zod';
import type { Look } from './look.js';

/**
 * Clubhouses: a player pays Gold to found one and becomes its leader. The
 * leader invites other players (who must accept) up to its capacity, and can
 * pay more Gold to raise that capacity. A player leads or belongs to at most
 * one clubhouse at a time, and it shows on their profile card.
 */

/** Gold to found a clubhouse. */
export const CLUBHOUSE_PRICE_GOLD = 500;

/** Members a fresh clubhouse holds before any capacity is bought. */
export const CLUBHOUSE_BASE_CAPACITY = 10;

/** Members each capacity purchase adds. */
export const CLUBHOUSE_CAPACITY_STEP = 2;

/** Gold for each capacity step, in the order they are bought. */
export const CLUBHOUSE_CAPACITY_PRICES: readonly number[] = Object.freeze([
  100, 150, 200, 250, 300, 400, 500, 600, 800, 1000,
]);

export const MAX_CLUBHOUSE_CAPACITY_STEPS = CLUBHOUSE_CAPACITY_PRICES.length;

/** Invites a clubhouse may have waiting for an answer at once. */
export const MAX_PENDING_CLUBHOUSE_INVITES = 20;

/** Total capacity for a clubhouse that has bought `extraSteps` capacity increases. */
export function clubhouseCapacity(extraSteps: number): number {
  return CLUBHOUSE_BASE_CAPACITY + Math.min(Math.max(Math.trunc(extraSteps), 0), MAX_CLUBHOUSE_CAPACITY_STEPS) * CLUBHOUSE_CAPACITY_STEP;
}

/** Gold the next capacity increase costs, or null once every step is bought. */
export function nextClubhouseCapacityPrice(extraSteps: number): number | null {
  return CLUBHOUSE_CAPACITY_PRICES[Math.max(Math.trunc(extraSteps), 0)] ?? null;
}

export const CLUBHOUSE_ROLES = ['leader', 'member'] as const;
export type ClubhouseRole = (typeof CLUBHOUSE_ROLES)[number];

export const clubhouseNameSchema = z
  .string()
  .trim()
  .min(3)
  .max(30)
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} '_-]*$/u, 'Use letters, numbers, spaces, hyphens, apostrophes or underscores');

export const createClubhouseSchema = z.object({ name: clubhouseNameSchema });

export const inviteToClubhouseSchema = z.object({ userId: z.string().min(1).max(64) });

export interface ClubhouseMember {
  id: string;
  displayName: string;
  look: Look;
  role: ClubhouseRole;
  joinedAt: number;
}

/** A pending invite, as the invited player sees it. */
export interface ClubhouseInvite {
  id: string;
  clubhouseId: string;
  clubhouseName: string;
  fromId: string;
  fromName: string;
  createdAt: number;
}

/** What a member sees when they open their own clubhouse. */
export interface ClubhouseView {
  id: string;
  name: string;
  leaderId: string;
  capacity: number;
  extraSteps: number;
  nextCapacityPrice: number | null;
  members: ClubhouseMember[];
  myRole: ClubhouseRole;
}

/** Shown on another player's profile card. */
export interface PublicClubhouse {
  id: string;
  name: string;
  role: ClubhouseRole;
  memberCount: number;
  capacity: number;
}

export const clubhouseServerMessageSchema = z.discriminatedUnion('t', [
  /** Your clubhouse or your invites changed (invited, joined, kicked, disbanded...): refetch. */
  z.object({ t: z.literal('clubhouse:changed') }),
]);
export type ClubhouseServerMessage = z.infer<typeof clubhouseServerMessageSchema>;
